/**
 * Activity Feed Component
 * Displays recent activities from all users (collaborative)
 */
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error,
  Person,
} from '@mui/icons-material';
import { useUpload } from '@/contexts/UploadContext';
import { useTranslation } from 'react-i18next';
import { formatTimeOnly } from '@/utils/dateUtils';

const ActivityFeed: React.FC = () => {
  const { t } = useTranslation();
  const { activityFeed } = useUpload();
  
  // Get icon for action type
  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'batch_created':
        return <CloudUpload />;
      case 'batch_completed':
        return <CheckCircle color="success" />;
      case 'batch_failed':
        return <Error color="error" />;
      default:
        return <Person />;
    }
  };
  
  // Translate action type
  const translateActionType = (actionType: string): string => {
    return t(`upload.activity.action.${actionType}`, actionType);
  };
  
  // Format date
  // Using Montreal timezone from utils
  const formatDate = formatTimeOnly;
  
  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6">
          {t('upload.activity.title', 'Recent Activity')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('upload.activity.subtitle', 'Collaborative feed from all users')}
        </Typography>
      </Box>
      
      <List sx={{ flexGrow: 1, overflow: 'auto', maxHeight: 600 }}>
        {activityFeed.length === 0 ? (
          <ListItem>
            <ListItemText
              primary={
                <Typography color="text.secondary" align="center">
                  {t('upload.activity.no_activity', 'No recent activity')}
                </Typography>
              }
            />
          </ListItem>
        ) : (
          activityFeed.map((activity) => (
            <ListItem key={activity.id} divider>
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'primary.light' }}>
                  {getActionIcon(activity.action_type)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={activity.message}
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Chip label={translateActionType(activity.action_type)} size="small" variant="outlined" />
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(activity.created_at)}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))
        )}
      </List>
    </Paper>
  );
};

export default ActivityFeed;

